import { useQuery } from '@tanstack/react-query';
import { getFetch } from '@legacy/utils/fetcher';
import { INotificationCenter } from '@legacy/apis/types/notificationCenter';

const NOTIFICATION_CENTER_URL_PATH = '/api/v2/notification-center';
export const NOTIFICATION_CENTER_MSW_STATUS_KEY = 'notificationCenterAPI';

export const NOTIFICATION_CENTER_KEY = 'NOTIFICATION_CENTER';

const getNotificationCenter = (): Promise<INotificationCenter> =>
  getFetch({
    path: NOTIFICATION_CENTER_URL_PATH,
    key: NOTIFICATION_CENTER_MSW_STATUS_KEY,
  });

const useGetNotificationCenterQuery = () =>
  useQuery({
    queryKey: [NOTIFICATION_CENTER_KEY],
    queryFn: getNotificationCenter,
  });

export const useGetIsApprovalEnabled = () => {
  const { data, ...restProps } = useGetNotificationCenterQuery();
  const isEnabled = data?.properties?.isApprovalEnabled;

  return {
    isApprovalEnabled: isEnabled || false,
    ...restProps,
  };
};

const formatData = (data?: INotificationCenter) => {
  const isApprovalOfTimeSheetEnabled =
    data?.properties?.isApprovalOfTimeSheetEnabled || false;
  const allApprovalItems =
    data?.properties?.notificationCenterApprovalItems || [];
  const timeSheetsToApprove = allApprovalItems.filter(
    (item) => item.navigationItemType === 0,
  );
  const absenceToApprove = allApprovalItems.filter(
    (item) => item.navigationItemType === 1,
  );
  const projectTimeToApprove = allApprovalItems.filter(
    (item) => item.navigationItemType === 2,
  );
  const projectTimeToApproveLength = projectTimeToApprove?.length || 0;
  const absenceToApproveLength = absenceToApprove?.length || 0;
  const timeSheetsToApproveLength = timeSheetsToApprove?.length || 0;

  const timeSheetsToBeClosed =
    data?.properties?.notificationCenterTimesheetItems || [];
  const timeSheetsToBeClosedLength =
    timeSheetsToBeClosed?.reduce(
      (acc, val) => acc + val.weeklyTimesheetDetails.length,
      0,
    ) || 0;

  // Get approval mode flags
  const isApprovalOfTimesheetByProjectManagerOnlyEnabled =
    data?.properties?.isApprovalOfTimesheetByProjectManagerOnlyEnabled ?? false;
  const isApprovalOfTimesheetByEmployeeManagerOnlyEnabled =
    data?.properties?.isApprovalOfTimesheetByEmployeeManagerOnlyEnabled ??
    false;
  const isApprovalOfTimesheetByProjectManagerThenEmployeeManagerEnabled =
    data?.properties
      ?.isApprovalOfTimesheetByProjectManagerThenEmployeeManagerEnabled ??
    false;
  const isManagerForActiveEmployees =
    data?.properties?.isManagerForActiveEmployees ?? false;

  // Check if user has project approval role
  const hasApprovalProjectRole =
    data?.properties?.hasApprovalProjectRole ?? false;

  // Calculate counts based on visibility logic (same as NotificationCenter component)
  let visibleTimeSheetsToBeClosedLength = 0;
  let visibleProjectTimeToApproveLength = 0;
  let visibleAbsenceToApproveLength = 0;
  let visibleTimesheetToApproveLength = 0;
  let itemsToTakeActionOn = 0;
  if (isApprovalOfTimeSheetEnabled) {
    // Timesheets to close - shown when employee manager only or combined mode
    if (
      (isApprovalOfTimesheetByEmployeeManagerOnlyEnabled ||
        isApprovalOfTimesheetByProjectManagerThenEmployeeManagerEnabled) &&
      timeSheetsToBeClosed.length > 0 &&
      isManagerForActiveEmployees
    ) {
      visibleTimeSheetsToBeClosedLength = timeSheetsToBeClosedLength;
    }

    // Project timesheets to approve - shown when project manager only or combined mode
    if (
      projectTimeToApprove &&
      (isApprovalOfTimesheetByProjectManagerOnlyEnabled ||
        isApprovalOfTimesheetByProjectManagerThenEmployeeManagerEnabled) &&
      hasApprovalProjectRole
    ) {
      visibleProjectTimeToApproveLength = projectTimeToApproveLength;
    }

    // Approve timesheets - shown when NOT in project manager only mode.
    if (
      timeSheetsToApprove &&
      !isApprovalOfTimesheetByProjectManagerOnlyEnabled &&
      isManagerForActiveEmployees
    ) {
      visibleTimesheetToApproveLength = timeSheetsToApproveLength;
    }

    // Absence to approve - if manager for active employees.
    if (absenceToApprove && isManagerForActiveEmployees) {
      visibleAbsenceToApproveLength = absenceToApproveLength;
    }

    itemsToTakeActionOn =
      visibleAbsenceToApproveLength +
      visibleProjectTimeToApproveLength +
      visibleTimeSheetsToBeClosedLength +
      visibleTimesheetToApproveLength +
      (data?.properties?.numberOfMileagePendingApproval || 0) +
      (data?.properties?.numberOfTravelExpensesPendingApproval || 0) +
      (data?.properties?.numberOfExpensesPendingApproval || 0);
  }
  return {
    projectTimeToApprove,
    absenceToApprove,
    timeSheetsToApprove,
    itemsToTakeActionOn,
    timeSheetsToBeClosedLength,
    timeSheetsToApproveLength,
  };
};

export const testFormatData = formatData;

export const useGetNotificationCenter = () => {
  const { data, ...restProps } = useGetNotificationCenterQuery();

  const {
    projectTimeToApprove,
    absenceToApprove,
    itemsToTakeActionOn,
    timeSheetsToBeClosedLength,
    timeSheetsToApprove,
    timeSheetsToApproveLength,
  } = formatData(data);

  return {
    ...data?.properties,
    ...restProps,
    isApprovalOfTimeSheetEnabled:
      data?.properties?.isApprovalOfTimeSheetEnabled || false,
    isApprovalOfExpensesEnabled:
      data?.properties?.isApprovalOfExpensesEnabled || false,
    isApprovalOfTimesheetByProjectManagerOnlyEnabled:
      data?.properties?.isApprovalOfTimesheetByProjectManagerOnlyEnabled ??
      false,
    isApprovalOfTimesheetByEmployeeManagerOnlyEnabled:
      data?.properties?.isApprovalOfTimesheetByEmployeeManagerOnlyEnabled ??
      false,
    isApprovalOfTimesheetByProjectManagerThenEmployeeManagerEnabled:
      data?.properties
        ?.isApprovalOfTimesheetByProjectManagerThenEmployeeManagerEnabled ??
      false,
    projectTimeToApprove,
    absenceToApprove,
    itemsToTakeActionOn,
    timeSheetsToBeClosedLength,
    timeSheetsToApprove,
    timeSheetsToApproveLength,
  };
};

export default getNotificationCenter;
